import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { DataTable, Pill, SelectField, Button, type Column } from "@atlas/ui/web";
import { downloadCsv, downloadJson } from "../../../lib/csv";
import { atlas } from "../../../lib/sdk";
import {
  PRODUTO_LABEL,
  fmtBRL,
  getBancoPerfil,
  type BancoProduto,
} from "../../../lib/banco-propostas";
import {
  CONTRATO_STATUS_LABEL,
  type Contrato,
  type ContratoStatus,
} from "../../../lib/banco-carteira";

function statusPill(s: ContratoStatus): "emdia" | "aceita" | "rejeitada" {
  return s === "em_dia" ? "emdia" : s === "quitado" ? "aceita" : "rejeitada";
}

/** Backend situacao -> ContratoStatus da UI. Contratos ainda em analise (reserva)
 *  nao entram na carteira — so os averbados/ativos aparecem aqui. */
function mapSituacaoBackend(situacao: string): ContratoStatus | null {
  const t = situacao.toLowerCase();
  if (t.includes("quitad")) return "quitado";
  if (t.includes("inadimpl")) return "inadimplente";
  // "libera" cobre "Liberada" — status usado pelo backend quando o banco
  // aprova a proposta e o contrato entra em vigor. Sem esse match, o
  // contrato sumia da carteira/ADF (ficava so na fila de propostas).
  if (t.includes("ativo") || t.includes("averb") || t.includes("libera")) return "em_dia";
  return null; // aguardando/cancelada/recusada nao vira contrato de carteira
}

const MESES_ABREV = ["Jan", "Fev", "Mar", "Abr", "Mai", "Jun", "Jul", "Ago", "Set", "Out", "Nov", "Dez"];

/** Data da proxima parcela — DD/Mmm/AAAA. Cliente pediu a data com dia. O dia e'
 *  o dia do mes do lancamento (as parcelas caem no aniversario do contrato),
 *  clampado ao ultimo dia do mes. O MES/ANO segue a mesma convencao do painel do
 *  servidor (servidores/index.ts:proximaParcelaDe): parcela 1 cai no mes do
 *  lancamento, entao proxima = mes do lancamento + parcelas ja pagas. Antes isso
 *  usava `new Date()+1mes` (hoje+1), que ignorava lancamento e parcelas pagas —
 *  banco mostrava Agosto enquanto o servidor mostrava Julho pro mesmo contrato.
 *  Contrato quitado (pagas >= total) mostra "Quitado" — nao ha proxima. */
function proximaParcelaDetalhe(parcelasPagas: number, totalParcelas: number, lancamento: string): string {
  if (totalParcelas > 0 && parcelasPagas >= totalParcelas) return "Quitado";
  const t = parseLancamento(lancamento);
  if (t <= 0) return "—";
  const base = new Date(t);
  const alvo = new Date(base.getFullYear(), base.getMonth() + parcelasPagas, 1);
  const ultimoDia = new Date(alvo.getFullYear(), alvo.getMonth() + 1, 0).getDate();
  const dia = Math.min(base.getDate(), ultimoDia);
  return `${String(dia).padStart(2, "0")}/${MESES_ABREV[alvo.getMonth()]}/${alvo.getFullYear()}`;
}

/** Parseia data em ISO 8601 ou DD/MM/YYYY (formato BR das fixtures do backend).
 *  Retorna 0 se nao parsear — sort trata como "muito antigo". */
function parseLancamento(raw: string | null | undefined): number {
  if (!raw) return 0;
  const iso = new Date(raw).getTime();
  if (!Number.isNaN(iso)) return iso;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(raw);
  if (m) return new Date(Number(m[3]), Number(m[2]) - 1, Number(m[1])).getTime();
  return 0;
}

/** Linha da carteira = Contrato + status do ADF na folha da prefeitura (folhaStatus),
 *  pra mostrar "Aplicada em folha" quando a prefeitura confirma (Passo 6). */
type CarteiraRow = Contrato & {
  folhaStatus?: "recebida" | "aplicada" | "falha";
  /** R2 key do CCB real (anexado pelo banco em /banco/propostas/:id). null se
   *  o contrato foi criado sem anexo — nesse caso a coluna CCB mostra "—". */
  ccbKey?: string | null;
};

function folhaPill(f?: "recebida" | "aplicada" | "falha"): { label: string; variant: "emdia" | "aceita" | "rejeitada" } {
  if (f === "aplicada") return { label: "Aplicada em folha", variant: "emdia" };
  if (f === "falha") return { label: "Falha na folha", variant: "rejeitada" };
  return { label: "Aguardando folha", variant: "aceita" };
}

export function BancoCarteira() {
  const perfil = getBancoPerfil();
  // Sem filtro de convenio aqui: a carteira segue o CONVENIO ATIVO selecionado
  // no topo (canto sup. esquerdo). O backend ja devolve so os contratos do
  // convenio ativo — mostrar todos/outros aqui era redundante e confuso
  // (cliente 30/07/2026).
  const [produto, setProduto] = useState<"" | BancoProduto>("");
  const [status, setStatus] = useState<"" | ContratoStatus>("");

  // Backend: contratos reais criados quando o banco aprovou propostas do
  // servidor. Poll 5s pra pegar novas aprovacoes em quase-tempo-real.
  const q = useQuery({
    queryKey: ["banco", "contratos-api"],
    queryFn: () => atlas.banco.contratos(),
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
    placeholderData: (prev) => prev,
  });

  const contratosBackend: CarteiraRow[] = useMemo(() => {
    const list = q.data?.contratos ?? [];
    return list
      .map((ct): CarteiraRow | null => {
        const s = mapSituacaoBackend(ct.situacao);
        if (!s) return null;
        const tipo = ct.tipoContrato?.toLowerCase() ?? "";
        const produtoUi: BancoProduto = tipo.includes("portab") || tipo.includes("refin")
          ? "portabilidade"
          : tipo.includes("econsig") || tipo.includes("cartao")
            ? "cartao"
            : "emprestimo";
        return {
          idUnico: ct.adf,
          cpfMasked: ct.cpfMasked,
          nome: ct.nome,
          convenio: ct.convenio,
          matricula: ct.matricula,
          produto: produtoUi,
          valor: ct.valorFinanciado,
          parcelas: ct.totalParcelas,
          valorParcela: ct.valorParcela,
          status: s,
          proximaParcela: proximaParcelaDetalhe(ct.parcelasPagas, ct.totalParcelas, ct.lancamento),
          // Prioridade: atualizadoEm (evento mais recente, ex.: acabou de
          // ser aprovada) > lancamento (data do contrato) > NOW. Assim
          // contratos recem-aprovados sobem pro topo em vez de ficarem
          // ancorados na data do contrato original.
          averbadoEm: ct.atualizadoEm ?? (() => {
            const t = parseLancamento(ct.lancamento);
            return t > 0 ? new Date(t).toISOString() : new Date().toISOString();
          })(),
          // Nao existe URL publica de CCB — o arquivo real esta em R2 privado e
          // e acessado via atlas.banco.fetchCcbBlob(ccbKey). Antes esta linha
          // apontava pra "https://formaliza.banco.com.br/ccb/..." que era 404.
          ccbUrl: "",
          ccbKey: ct.ccbKey ?? null,
          folhaStatus: ct.folhaStatus, // Passo 6: prefeitura confirmou em folha?
        };
      })
      .filter((c): c is CarteiraRow => c !== null);
  }, [q.data]);

  const contratos = useMemo(() => {
    // Fonte unica = backend. Antes concatenavamos CARTEIRA_SEED (Sonia,
    // Paulo, Regina, etc. hardcoded) — visivel para QUALQUER banco logado,
    // incluindo recem criado pela averbadora que deveria estar zerado.
    // Removido: um banco so ve os contratos que ele proprio averbou.
    const byId = new Map<string, CarteiraRow>();
    for (const c of contratosBackend) byId.set(c.idUnico, c);
    return [...byId.values()]
      .filter((c) => {
        if (produto && c.produto !== produto) return false;
        if (status && c.status !== status) return false;
        return true;
      })
      // Recentes no topo — ordena por averbadoEm desc. Aceita ISO e DD/MM/YYYY
      // (as fixtures do backend usam formato BR "18/03/2026", que new Date()
      // sozinho retorna NaN e joga o contrato pro fim da lista).
      .sort((a, b) => parseLancamento(b.averbadoEm) - parseLancamento(a.averbadoEm));
  }, [produto, status, contratosBackend]);

  const exportRows = () =>
    contratos.map((c) => ({
      idUnico: c.idUnico,
      cpf: c.cpfMasked,
      nome: c.nome,
      convenio: c.convenio,
      matricula: c.matricula,
      produto: PRODUTO_LABEL[c.produto],
      valor: c.valor,
      parcelas: c.parcelas,
      valorParcela: c.valorParcela,
      status: CONTRATO_STATUS_LABEL[c.status],
      proximaParcela: c.proximaParcela,
      // ADF = idUnico do contrato (mesmo dado, mesmo campo no domain).
      adf: c.idUnico,
      // No CSV, exporta o nome do arquivo CCB (ultima parte do R2 key) ou
      // "—" quando nao ha CCB anexado. Antes exportava uma URL fake que nunca
      // resolvia — usuario baixava CSV e todos os links davam 404.
      ccb: c.ccbKey ? (c.ccbKey.split("/").pop() ?? c.ccbKey) : "—",
    }));

  const columns: Column<CarteiraRow>[] = [
    { key: "status", header: "Status", render: (r) => <Pill variant={statusPill(r.status)}>{CONTRATO_STATUS_LABEL[r.status]}</Pill> },
    {
      key: "folha",
      header: "Folha",
      render: (r) => {
        const f = folhaPill(r.folhaStatus);
        return <Pill variant={f.variant}>{f.label}</Pill>;
      },
    },
    // ADF = ID unico do contrato no domain. Antes tinham duas colunas
    // apontando pro mesmo dado (uma real, outra derivada de localStorage);
    // agora e' uma so, sempre presente. Cliente 28/07/2026.
    { key: "adf", header: "ADF", mono: true, render: (r) => r.idUnico },
    {
      key: "nome",
      header: "Servidor",
      render: (r) => (
        <>
          <div>{r.nome}</div>
          <div style={{ fontSize: 11, color: "var(--text-dim)" }}>{r.cpfMasked} / {r.matricula}</div>
        </>
      ),
    },
    { key: "convenio", header: "Convênio" },
    { key: "valor", header: "Valor", align: "right", render: (r) => fmtBRL(r.valor) },
    { key: "parcelas", header: "Parcelas", align: "right", render: (r) => `${r.parcelas}x de ${fmtBRL(r.valorParcela)}` },
    { key: "proximaParcela", header: "Próx. parcela", render: (r) => r.proximaParcela },
    {
      key: "ccb",
      header: "CCB",
      render: (r) => <CcbCell ccbKey={r.ccbKey ?? null} />,
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Carteira
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.8rem" }}>Meus contratos</h1>
          <p style={{ color: "var(--text-muted)", margin: "6px 0 0", maxWidth: 640 }}>
            Contratos averbados por convênio. O <strong>ID único</strong> é a chave de conciliação de cada operação.
          </p>
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
          {perfil.perms.exportacao ? (
            <>
              <Button variant="ghost" size="sm" onClick={() => downloadCsv("carteira-banco.csv", exportRows())}>Exportar CSV</Button>
              <Button variant="ghost" size="sm" onClick={() => downloadJson("carteira-banco.json", exportRows())}>Exportar JSON</Button>
            </>
          ) : (
            <span style={{ fontSize: 12, color: "var(--text-dim)" }}>Perfil sem permissão de exportação</span>
          )}
        </div>
      </header>

      <div style={{ display: "flex", gap: 12, alignItems: "end", flexWrap: "wrap" }}>
        <SelectField
          label="Produto"
          value={produto}
          onChange={(e) => setProduto(e.target.value as "" | BancoProduto)}
          options={[
            { value: "", label: "Todos" },
            { value: "emprestimo", label: PRODUTO_LABEL.emprestimo },
            { value: "cartao", label: PRODUTO_LABEL.cartao },
            { value: "portabilidade", label: PRODUTO_LABEL.portabilidade },
          ]}
        />
        <SelectField
          label="Status"
          value={status}
          onChange={(e) => setStatus(e.target.value as "" | ContratoStatus)}
          options={[
            { value: "", label: "Todos" },
            { value: "em_dia", label: CONTRATO_STATUS_LABEL.em_dia },
            { value: "inadimplente", label: CONTRATO_STATUS_LABEL.inadimplente },
            { value: "quitado", label: CONTRATO_STATUS_LABEL.quitado },
          ]}
        />
      </div>

      {q.isError ? (
        // Erro precede vazio: sem esse aviso, uma falha de fetch mostrava a
        // tabela vazia ("Nenhum contrato") como se a carteira estivesse zerada.
        // Polling de 5s se recupera. Convencao do projeto (30/07/2026).
        <div style={{ padding: "12px 16px", borderRadius: 10, border: "1px solid var(--danger-500)", color: "var(--danger-500)", fontSize: 13, marginBottom: 12 }}>
          Não foi possível carregar os contratos. Tentando novamente…
        </div>
      ) : null}
      <DataTable columns={columns} rows={contratos} rowKey={(r) => r.idUnico} emptyState="Nenhum contrato com esses filtros." />
    </div>
  );
}

/** Celula da coluna CCB — se o contrato tem arquivo anexado (ccbKey), oferece
 *  botao "Abrir" que baixa o blob autenticado do R2 e abre em nova aba.
 *  Sem ccbKey, mostra "—" (contrato averbado antes do upload virar obrigatorio,
 *  ou contrato de reserva que ainda nao teve anexo). */
function CcbCell({ ccbKey }: { ccbKey: string | null }) {
  const [abrindo, setAbrindo] = useState(false);
  if (!ccbKey) return <span style={{ color: "var(--text-dim)", fontSize: 13 }}>—</span>;
  const abrir = async () => {
    setAbrindo(true);
    try {
      const blob = await atlas.banco.fetchCcbBlob(ccbKey);
      const url = URL.createObjectURL(blob);
      window.open(url, "_blank", "noopener,noreferrer");
      // Revoga em 30s pra nao vazar memoria em sessoes longas.
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch {
      // Silencioso — o operador ve que nada abriu; pode reabrir na pagina de detalhe
      // da proposta, que tem tratamento de erro visivel.
    } finally {
      setAbrindo(false);
    }
  };
  return (
    <Button size="sm" variant="ghost" onClick={abrir} disabled={abrindo}>
      {abrindo ? "..." : "Abrir"}
    </Button>
  );
}
